import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { ShieldCheck, ArrowRight, Spinner } from "@phosphor-icons/react";
import { Helmet } from "react-helmet-async";
import { adminLogin, getAdminToken } from "@/lib/adminApi";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const hasToken = !!getAdminToken();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await adminLogin(email.trim(), password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Login failed. Check email and password.");
    } finally {
      setBusy(false);
    }
  };

  if (hasToken) return <Navigate to="/admin" replace />;

  return (
    <div className="min-h-screen bg-zinc-950 text-white grid place-items-center px-4 relative overflow-hidden">
      <Helmet>
        <title>Admin Login · TechConnect LLC</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(37,99,235,0.18),transparent_55%)]" />

      <div className="relative w-full max-w-md">
        <div className="flex items-center gap-2 justify-center mb-6">
          <div className="h-10 w-10 rounded-xl bg-blue-600 grid place-items-center">
            <ShieldCheck size={20} weight="fill" />
          </div>
          <span className="font-heading text-xl font-extrabold tracking-tight">
            TechConnect Admin
          </span>
        </div>

        <form
          onSubmit={submit}
          data-testid="admin-login-form"
          className="bg-zinc-900/70 backdrop-blur border border-white/10 rounded-2xl p-8 shadow-2xl"
        >
          <h1 className="font-heading text-2xl font-bold">Sign in</h1>
          <p className="text-sm text-zinc-400 mt-1">Owner-only dashboard.</p>

          <div className="mt-6 space-y-3">
            <label className="block">
              <span className="text-sm text-zinc-300">Email</span>
              <input
                data-testid="admin-email"
                required
                autoComplete="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-xl bg-zinc-950 border border-white/10 text-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </label>
            <label className="block">
              <span className="text-sm text-zinc-300">Password</span>
              <input
                data-testid="admin-password"
                required
                autoComplete="current-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full rounded-xl bg-zinc-950 border border-white/10 text-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </label>
          </div>

          {error && (
            <div
              data-testid="admin-login-error"
              className="mt-4 rounded-lg bg-rose-500/15 border border-rose-500/30 text-rose-200 px-3 py-2 text-sm"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit"
            className="mt-6 group w-full inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white px-6 py-3.5 font-semibold transition-all"
          >
            {busy ? (
              <>
                <Spinner size={16} className="animate-spin" /> Signing in…
              </>
            ) : (
              <>
                Sign in
                <ArrowRight
                  size={18}
                  weight="bold"
                  className="transition-transform group-hover:translate-x-1"
                />
              </>
            )}
          </button>
        </form>

        <a
          href="/"
          className="mt-6 block text-center text-sm text-zinc-400 hover:text-white transition-colors"
        >
          ← Back to website
        </a>
      </div>
    </div>
  );
}
